import { InternalArpMedia, MintStatusModel } from '@arpeggi/shared'
import { observer } from 'mobx-react-lite'
import { FC, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { IArpMedia } from '../../../../../features/arp-registry/ArpMedia'
import ArpRegistry from '../../../../../features/arp-registry/ArpRegistry'
import { styled } from '../../../../lib/stitches'
import { fetchArpWaveform } from '../../../../stores/registry/RegistryUtils'
import { useTreeState } from '../../../../stores/Root'
import SharedAudioContext from '../../../../stores/SharedAudioContext'
import CenteredLoadingAnimation from '../../../core/CenteredLoadingAnimation'
import { PauseIcon, PlayIcon } from '../../../core/icons'
import AudioVisualizer from '../../../player/AudioVisualizer'
import { formatTime } from '../../../player/PlayerUtils'
import ActionsColumn from './ActionsColumn'
import DetailsColumn from './DetailsColumn'
import MintInProgressRow from './MintInProgressRow'
import ProfileAvatar from './ProfileAvatar'
import Row from './Row'
import Table from './Table'
import { TableProps, useAsyncEffect } from './utils'

interface SongRowProps {
  song: IArpMedia
  active?: boolean
  onClick?: () => void
  hideRowPlayControls?: boolean
}

const StyledVisualizerContainer = styled('div', {
  width: '100%',
  height: '36px',
  display: 'flex',
  opacity: 0.3,
  position: 'relative',
  overflow: 'hidden'
})
const VISUALIZER_OPTIONS = {
  color: '#000000'
}

const FAKE_SONG = {
  dataUri: `https://d2hgqm22ttdu22.cloudfront.net/cc0-samples-wav2/013_Sabriel_Vocal_Pack/Vox/Sab_NoOnesHomie2.wav`
}

const VisualizerContainer = StyledVisualizerContainer

const FakeSongVisualizer: FC<{ playing?: boolean }> = observer(() => {
  const [buffer, setBuffer] = useState<AudioBuffer>()
  useAsyncEffect(async () => {
    const wave = await fetchArpWaveform(FAKE_SONG as InternalArpMedia)
    const decodedWave = await SharedAudioContext.decodeAudioData(wave)
    setBuffer(decodedWave)
  }, [])

  return (
    <VisualizerContainer id="visualizer">
      {buffer && <AudioVisualizer audioBuffer={buffer} options={VISUALIZER_OPTIONS} />}
    </VisualizerContainer>
  )
})

const segmentTrackButton = async (index: number, button: string) => {
  let location = window.location.pathname.includes('explore')
    ? 'explore table'
    : window.location.pathname.includes('home')
    ? 'home table'
    : window.location.pathname.includes('profile')
    ? 'profile table'
    : 'unknown'
  const songDetails = await ArpRegistry.getByIdAsync(index)
  window.analytics.track('Button Clicked', {
    button: button,
    token: index,
    source: window.location.pathname,
    song: songDetails.metadata.title,
    location: location,
    type: 'song',
    artistName: songDetails.user.displayName,
    artistEthAddress: songDetails.user.walletAddress,
    genres: songDetails.metadata.genres,
    bpm: songDetails.metadata.bpm,
    tags: songDetails.metadata.tags
  })
}

const StyledPlayIcon = styled(PlayIcon, {
  position: 'absolute',
  left: '16px'
})
const StyledPauseIcon = styled(PauseIcon, {
  position: 'absolute',
  left: '16px'
})
const Play = StyledPlayIcon
const Pause = StyledPauseIcon

export const SongRow: FC<SongRowProps> = observer(
  ({ song, active, onClick, hideRowPlayControls }) => {
    const navigate = useNavigate()
    const { player } = useTreeState()
    const artistName = song.user?.displayName
    const genres = song.metadata.genres.length > 0 ? song.metadata.genres.join(', ') : '-'
    const [hover, setHover] = useState<boolean>(false)

    let indicator = null
    if (!hideRowPlayControls) {
      if (active && player.isPlaying) indicator = <Pause />
      if ((active && !player.isPlaying) || (!active && hover)) indicator = <Play />
    }
    return (
      <Row
        isActive={active}
        onMouseOver={() => setHover(true)}
        onMouseOut={() => setHover(false)}
        onClick={() => {
          onClick()
          segmentTrackButton(song.id, 'Table Entry')
        }}
        columns={{
          indicator,
          avatar: (
            <ProfileAvatar
              eth={song.user.walletAddress}
              style={{ width: '26px', height: '26px' }}
            />
          ),
          details: (
            <DetailsColumn
              title={song.metadata.title}
              artistEthAddress={song.user.walletAddress}
              artistName={artistName}
            />
          ),
          visualizer: <FakeSongVisualizer />,
          genre: <Table.Text>{genres}</Table.Text>,
          remixCount: <Table.Text>{song.remixCount}</Table.Text>,
          actions: (
            <ActionsColumn
              song={song}
              isHovered={hover}
              handleMore={() => navigate(`/song?token=${song.id}`)}
              songLengthFormatted={formatTime(player.song?.lengthInSeconds)}
            />
          )
        }}
      />
    )
  }
)

const SongTable: FC<
  Omit<TableProps, 'data'> & {
    data: (IArpMedia | MintStatusModel)[]
    onSelectMintInProgress?: (jobId: string) => void
  }
> = ({
  data,
  onSelect,
  selected,
  loading,
  onDropdownSelect,
  onSelectMintInProgress,
  hideRowPlayControls
}) => {
  return (
    <Table.Root>
      <Table.Header
        css={{
          display: 'none',
          '@tablet': { display: 'flex' }
        }}
      >
        <Table.Col css={{ flex: 1 }}>
          <Table.Text bold>{'Title'}</Table.Text>
        </Table.Col>
        <Table.Col css={{ width: '120px', marginLeft: '32px' }}>
          <Table.Text bold>{'Genre'}</Table.Text>
        </Table.Col>
        <Table.Col
          css={{
            width: '80px',
            marginLeft: '32px',
            '@bigscreen': { width: '100px' }
          }}
        >
          <Table.Text bold>{'# Remixes'}</Table.Text>
        </Table.Col>
        <Table.Col
          css={{
            marginLeft: '32px',
            minWidth: '120px',
            width: '120px',

            '@bigscreen': {
              minWidth: '160px',
              width: '120px'
            }
          }}
        />
      </Table.Header>
      <Table.Body>
        {loading ? (
          <CenteredLoadingAnimation />
        ) : (
          data.map((songOrMintStatusModel) => {
            if (!songOrMintStatusModel) {
              return null
            }
            if ('jobId' in songOrMintStatusModel) {
              const mintStatusEntity = songOrMintStatusModel as MintStatusModel
              return (
                <MintInProgressRow
                  key={mintStatusEntity.jobId}
                  mintStatusEntity={mintStatusEntity}
                  onClick={() => onSelectMintInProgress(mintStatusEntity.jobId)}
                  songPreview={true}
                />
              )
            }

            const song = songOrMintStatusModel as IArpMedia
            return (
              <SongRow
                song={song}
                key={song.id}
                onClick={() => onSelect(song.id)}
                active={selected === song.id}
                hideRowPlayControls={hideRowPlayControls}
              />
            )
          })
        )}
      </Table.Body>
    </Table.Root>
  )
}

export default observer(SongTable)
